import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  FileText,
  ExternalLink,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { apiService } from '../../services/apiService';
import { MethodNode, PartMtlNode, PartOpr } from '../../services/types';
import { parseCalendarDate } from '../../utils/calendarDate';

// Serializable state for caching
export interface MethodTreeState {
  expandedMaterials: string[];
  loadedChildren: Record<string, MethodNode | null>;
}

interface MethodTreeProps {
  methodData: MethodNode;
  onMaterialExpand?: (partNum: string) => Promise<MethodNode | null>;
  initialState?: MethodTreeState;
  onStateChange?: (state: MethodTreeState) => void;
}

/**
 * Recursively collect all expandable material part numbers from initial data
 */
function collectExpandablePartNums(
  materials: PartMtlNode[],
  result: Set<string> = new Set()
): Set<string> {
  for (const mtl of materials) {
    if (mtl.expandTree) {
      result.add(mtl.partNum);
      if (mtl.materials && mtl.materials.length > 0) {
        collectExpandablePartNums(mtl.materials, result);
      }
    }
  }
  return result;
}

/**
 * Format operation value with appropriate unit display
 */
function formatOprValue(value: number, format: string): string {
  if (!format || value === 0) return '-';

  let formattedValue: string;
  if (format === 'HR' || format === 'HP') {
    formattedValue = `${value}h`;
  } else if (format === 'MP' || format === 'OM') {
    formattedValue = `${value}m`;
  } else {
    formattedValue = value.toLocaleString();
  }

  return formattedValue;
}

/**
 * Format setup hours
 */
function formatSetupHours(hours: number): string {
  if (hours === 0) return '0h';
  return `${hours}h`;
}

/**
 * Get background color class based on depth for visual hierarchy
 */
function getDepthBgClass(depth: number): string {
  return depth % 2 === 1 ? 'bg-gray-50' : '';
}

/**
 * Method/BOM Tree Component
 * Displays hierarchical operations and materials sections with their own headers
 */
export const MethodTree: React.FC<MethodTreeProps> = ({
  methodData,
  onMaterialExpand,
  initialState,
  onStateChange,
}) => {
  const { t } = useTranslation();
  const [expandedMaterials, setExpandedMaterials] = useState<Set<string>>(
    () => new Set(initialState?.expandedMaterials || [])
  );
  const [loadingMaterials, setLoadingMaterials] = useState<Set<string>>(
    new Set()
  );
  const [loadedChildren, setLoadedChildren] = useState<
    Map<string, MethodNode | null>
  >(() => new Map(Object.entries(initialState?.loadedChildren || {})));

  // Drawing availability state (null = checking, true/false = result)
  const [drawingAvailable, setDrawingAvailable] = useState<boolean | null>(
    null
  );

  useEffect(() => {
    const drawNum = methodData.drawNum?.trim();
    if (!drawNum) {
      setDrawingAvailable(false);
      return;
    }

    let cancelled = false;
    setDrawingAvailable(null);

    apiService.checkDrawingAvailable(drawNum).then((available) => {
      if (!cancelled) setDrawingAvailable(available);
    });

    return () => {
      cancelled = true;
    };
  }, [methodData.drawNum]);

  // Track if we've applied initial state (to avoid re-applying after user actions)
  const hasAppliedInitialState = useRef(!!initialState);

  // Sync with initialState when it becomes available (e.g., after cache restore)
  useEffect(() => {
    // Only apply if we haven't already and initialState is now available
    if (!hasAppliedInitialState.current && initialState) {
      setExpandedMaterials(new Set(initialState.expandedMaterials));
      setLoadedChildren(
        new Map(Object.entries(initialState.loadedChildren || {}))
      );
      hasAppliedInitialState.current = true;
    }
  }, [initialState]);

  // Helper to notify parent of state changes (called explicitly in handlers)
  const notifyStateChange = useCallback(
    (
      newExpanded: Set<string>,
      newLoadedChildren: Map<string, MethodNode | null>
    ) => {
      if (onStateChange) {
        onStateChange({
          expandedMaterials: Array.from(newExpanded),
          loadedChildren: Object.fromEntries(newLoadedChildren),
        });
      }
    },
    [onStateChange]
  );

  // Collect all expandable part numbers from initial data
  const initialExpandablePartNums = useMemo(() => {
    return collectExpandablePartNums(methodData.materials);
  }, [methodData.materials]);

  const handleExpandAll = useCallback(async () => {
    if (!onMaterialExpand) {
      const newExpanded = new Set(initialExpandablePartNums);
      setExpandedMaterials(newExpanded);
      notifyStateChange(newExpanded, loadedChildren);
      return;
    }

    // Build set of all items to expand
    const allToExpand = new Set(initialExpandablePartNums);
    const allLoadedChildren = new Map(loadedChildren);

    // Add expandable items from already loaded children
    loadedChildren.forEach((node) => {
      if (node?.materials) {
        node.materials.forEach((mtl) => {
          if (mtl.expandTree) allToExpand.add(mtl.partNum);
        });
      }
    });

    // Expand initial level immediately
    setExpandedMaterials(new Set(allToExpand));

    // Keep loading until all expandable items are loaded
    let hasMoreToLoad = true;
    while (hasMoreToLoad) {
      // Find items that need to be loaded
      const toLoad = Array.from(allToExpand).filter(
        (partNum) => !allLoadedChildren.has(partNum)
      );

      if (toLoad.length === 0) {
        hasMoreToLoad = false;
        break;
      }

      setLoadingMaterials(new Set(toLoad));

      // Fetch all in parallel
      const results = await Promise.all(
        toLoad.map(async (partNum) => {
          try {
            const data = await onMaterialExpand(partNum);
            return { partNum, data };
          } catch (error) {
            console.error(`Failed to load ${partNum}:`, error);
            return { partNum, data: null };
          }
        })
      );

      // Update loaded children and collect nested expandable items
      let foundNewExpandable = false;
      results.forEach(({ partNum, data }) => {
        allLoadedChildren.set(partNum, data);
        // Collect expandable items from newly loaded data
        if (data?.materials) {
          data.materials.forEach((mtl) => {
            if (mtl.expandTree && !allToExpand.has(mtl.partNum)) {
              allToExpand.add(mtl.partNum);
              foundNewExpandable = true;
            }
          });
        }
      });

      // Update state after each level so UI expands progressively
      setLoadedChildren(new Map(allLoadedChildren));
      setExpandedMaterials(new Set(allToExpand));

      // If no new expandable items were found, we're done
      if (!foundNewExpandable) {
        hasMoreToLoad = false;
      }
    }

    setLoadingMaterials(new Set());
    // Notify parent of final state
    notifyStateChange(allToExpand, allLoadedChildren);
  }, [
    initialExpandablePartNums,
    onMaterialExpand,
    loadedChildren,
    notifyStateChange,
  ]);

  const handleCollapseAll = useCallback(() => {
    // Clear ALL expanded materials - this collapses everything at all levels
    const newExpanded = new Set<string>();
    setExpandedMaterials(newExpanded);
    // Preserve loaded children so they don't need to be re-fetched
    notifyStateChange(newExpanded, loadedChildren);
  }, [loadedChildren, notifyStateChange]);

  const handleMaterialToggle = useCallback(
    async (partNum: string, hasLoadedData: boolean) => {
      const isCurrentlyExpanded = expandedMaterials.has(partNum);

      // Calculate new expanded state
      const newExpanded = new Set(expandedMaterials);
      if (isCurrentlyExpanded) {
        newExpanded.delete(partNum);
      } else {
        newExpanded.add(partNum);
      }

      // Track loaded children for state change notification
      let newLoadedChildren = loadedChildren;

      if (!isCurrentlyExpanded && !hasLoadedData && onMaterialExpand) {
        setLoadingMaterials((prev) => new Set(prev).add(partNum));
        try {
          const childData = await onMaterialExpand(partNum);
          newLoadedChildren = new Map(loadedChildren).set(partNum, childData);
          setLoadedChildren(newLoadedChildren);
        } catch (error) {
          console.error('Failed to load child data:', error);
          newLoadedChildren = new Map(loadedChildren).set(partNum, null);
          setLoadedChildren(newLoadedChildren);
        } finally {
          setLoadingMaterials((prev) => {
            const next = new Set(prev);
            next.delete(partNum);
            return next;
          });
        }
      }

      setExpandedMaterials(newExpanded);
      notifyStateChange(newExpanded, newLoadedChildren);
    },
    [expandedMaterials, loadedChildren, onMaterialExpand, notifyStateChange]
  );

  // Check if anything is expanded (for button state)
  const hasAnyExpanded = expandedMaterials.size > 0;
  const hasExpandableItems = initialExpandablePartNums.size > 0;
  const isExpandingAll = loadingMaterials.size > 1;

  // Render operations section header row
  const renderOperationsHeader = (
    count: number,
    depth: number,
    parentKey: string
  ): React.ReactNode => {
    const indent = depth * 32;
    return (
      <tr
        key={`ops-header-${parentKey}-${depth}`}
        className="bg-amber-100/70 border-l-4 border-l-amber-300"
      >
        <td
          className="px-3 py-2 text-sm font-semibold text-amber-800 border-b border-amber-200"
          style={{ paddingLeft: indent + 12 }}
        >
          {t('parts.method.operations')} ({count})
        </td>
        <td className="px-3 py-2 text-xs font-semibold text-amber-700 border-b border-amber-200">
          {t('parts.method.operationsTable.description')}
        </td>
        <td className="px-3 py-2 text-xs font-semibold text-amber-700 border-b border-amber-200 text-center">
          {t('parts.method.operationsTable.setup')}
        </td>
        <td className="px-3 py-2 text-xs font-semibold text-amber-700 border-b border-amber-200 text-center">
          {t('parts.method.operationsTable.opr')}
        </td>
        <td className="px-3 py-2 text-xs font-semibold text-amber-700 border-b border-amber-200 text-center">
          {t('parts.method.operationsTable.format')}
        </td>
        <td
          colSpan={3}
          className="px-3 py-2 border-b border-amber-200 bg-amber-50/50"
        />
      </tr>
    );
  };

  // Render single operation row
  const renderOperationRow = (
    op: PartOpr,
    depth: number,
    parentKey: string,
    index: number
  ): React.ReactNode => {
    const indent = depth * 32;
    const formatLabel =
      op.stdFormat && t(`parts.method.stdFormats.${op.stdFormat}`)
        ? t(`parts.method.stdFormats.${op.stdFormat}`)
        : op.stdFormat || '-';

    // Visual depth indicator
    const borderClass =
      depth === 0
        ? 'border-l-4 border-l-amber-100'
        : depth === 1
          ? 'border-l-4 border-l-amber-200'
          : 'border-l-4 border-l-amber-300';

    return (
      <tr
        key={`op-${parentKey}-${op.opSeq}-${index}`}
        className={`bg-amber-50/50 hover:bg-amber-100/50 ${borderClass} ${getDepthBgClass(depth)}`}
      >
        <td
          className="px-3 py-2 text-sm border-b border-gray-200"
          style={{ paddingLeft: indent + 12 }}
        >
          <div className="flex items-center">
            {/* Spacer to align with material expand button */}
            <div className="w-5 flex-shrink-0 mr-1" />
            <span className="font-medium text-amber-800">
              OP{op.opSeq} - {op.opCode}
            </span>
          </div>
        </td>
        <td className="px-3 py-2 text-sm border-b border-gray-200 text-gray-700">
          {op.opDesc}
        </td>
        <td className="px-3 py-2 text-sm border-b border-gray-200 text-center">
          {formatSetupHours(op.estSetHours)}
        </td>
        <td className="px-3 py-2 text-sm border-b border-gray-200 text-center">
          {formatOprValue(op.prodStandard, op.stdFormat)}
        </td>
        <td className="px-3 py-2 text-sm border-b border-gray-200 text-center text-gray-600">
          {formatLabel}
        </td>
        <td colSpan={3} className="px-3 py-2 border-b border-gray-200" />
      </tr>
    );
  };

  // Render materials section header row
  const renderMaterialsHeader = (
    count: number,
    depth: number,
    parentKey: string
  ): React.ReactNode => {
    const indent = depth * 32;
    return (
      <tr
        key={`mtls-header-${parentKey}-${depth}`}
        className="bg-blue-100 border-l-4 border-l-blue-400"
      >
        <td
          className="px-3 py-2 text-sm font-semibold text-blue-800 border-b border-blue-200"
          style={{ paddingLeft: indent + 12 }}
        >
          {t('parts.method.materials')} ({count})
        </td>
        <td className="px-3 py-2 text-xs font-semibold text-blue-700 border-b border-blue-200">
          {t('parts.method.materialsTable.description')}
        </td>
        <td className="px-3 py-2 text-xs font-semibold text-blue-700 border-b border-blue-200 text-center">
          {t('parts.method.materialsTable.qtyPer')}
        </td>
        <td className="px-3 py-2 text-xs font-semibold text-blue-700 border-b border-blue-200 text-center">
          {t('parts.method.materialsTable.onHand')}
        </td>
        <td className="px-3 py-2 text-xs font-semibold text-blue-700 border-b border-blue-200 text-center">
          {t('parts.method.materialsTable.demand')}
        </td>
        <td className="px-3 py-2 text-xs font-semibold text-blue-700 border-b border-blue-200 text-center">
          {t('parts.method.materialsTable.availNow')}
        </td>
        <td className="px-3 py-2 text-xs font-semibold text-blue-700 border-b border-blue-200 text-center">
          {t('parts.method.materialsTable.supply')}
        </td>
        <td className="px-3 py-2 text-xs font-semibold text-blue-700 border-b border-blue-200 text-center">
          {t('parts.method.materialsTable.availLater')}
        </td>
      </tr>
    );
  };

  // Render single material row
  const renderMaterialRow = (
    mtl: PartMtlNode,
    depth: number,
    parentKey: string,
    index: number
  ): React.ReactNode[] => {
    const indent = depth * 32;
    const isExpanded = expandedMaterials.has(mtl.partNum);
    const isLoading = loadingMaterials.has(mtl.partNum);
    const canExpand = mtl.expandTree;

    const lazyLoadedData = loadedChildren.get(mtl.partNum);
    const hasLoadedData = loadedChildren.has(mtl.partNum);

    const childOps = lazyLoadedData?.operations || mtl.operations || [];
    const childMtls = lazyLoadedData?.materials || mtl.materials || [];

    const rows: React.ReactNode[] = [];

    const mtlKey = `${parentKey}-${mtl.partNum}-${mtl.mtlSeq}`;

    // Visual depth indicator
    const borderClass =
      depth === 0
        ? 'border-l-4 border-l-blue-200'
        : depth === 1
          ? 'border-l-4 border-l-blue-300'
          : 'border-l-4 border-l-blue-400';

    // Material data row
    rows.push(
      <tr
        key={`mtl-${mtlKey}-${index}`}
        className={`hover:bg-blue-50 ${borderClass} ${getDepthBgClass(depth)}`}
      >
        <td
          className="px-3 py-2 text-sm border-b border-gray-200"
          style={{ paddingLeft: indent + 12 }}
        >
          <div className="flex items-center">
            <div className="w-5 flex-shrink-0 flex items-center justify-center mr-1">
              {canExpand ? (
                <button
                  onClick={() =>
                    handleMaterialToggle(mtl.partNum, hasLoadedData)
                  }
                  className="p-0.5 hover:bg-gray-200 rounded transition-colors"
                  title={t('common.expandCollapse')}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                  ) : isExpanded ? (
                    <ChevronDown size={16} className="text-gray-600" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-600" />
                  )}
                </button>
              ) : null}
            </div>
            <Link
              to={`/parts/${encodeURIComponent(mtl.partNum)}`}
              className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
            >
              {mtl.partNumUM}
            </Link>
          </div>
        </td>
        <td className="px-3 py-2 text-sm border-b border-gray-200 text-gray-700 truncate max-w-xs">
          {mtl.partDescription}
        </td>
        <td className="px-3 py-2 text-sm border-b border-gray-200 text-center font-medium">
          {mtl.qtyPer.toLocaleString()}
        </td>
        <td className="px-3 py-2 text-sm border-b border-gray-200 text-center">
          {mtl.onHandQty.toLocaleString()}
        </td>
        <td className="px-3 py-2 text-sm border-b border-gray-200 text-center">
          {mtl.demandQty.toLocaleString()}
        </td>
        <td
          className={`px-3 py-2 text-sm border-b border-gray-200 text-center font-medium ${
            mtl.availNowQty < 0
              ? 'text-red-600'
              : mtl.availNowQty > 0
                ? 'text-green-600'
                : ''
          }`}
        >
          {mtl.availNowQty.toLocaleString()}
        </td>
        <td className="px-3 py-2 text-sm border-b border-gray-200 text-center">
          {mtl.supplyQty.toLocaleString()}
        </td>
        <td
          className={`px-3 py-2 text-sm border-b border-gray-200 text-center font-medium ${
            mtl.availLaterQty < 0
              ? 'text-red-600'
              : mtl.availLaterQty > 0
                ? 'text-green-600'
                : ''
          }`}
        >
          {mtl.availLaterQty.toLocaleString()}
        </td>
      </tr>
    );

    // Child content if expanded
    if (isExpanded && canExpand && !isLoading) {
      const hasOps = childOps.length > 0;
      const hasMtls = childMtls.length > 0;

      // Child operations section
      if (hasOps) {
        rows.push(renderOperationsHeader(childOps.length, depth + 1, mtlKey));
        childOps.forEach((op, idx) => {
          rows.push(renderOperationRow(op, depth + 1, mtlKey, idx));
        });
      }

      // Child materials section
      if (hasMtls) {
        rows.push(renderMaterialsHeader(childMtls.length, depth + 1, mtlKey));
        childMtls.forEach((childMtl, idx) => {
          rows.push(...renderMaterialRow(childMtl, depth + 1, mtlKey, idx));
        });
      }

      // Show "no items" only if we've loaded data and it's empty
      if (!hasOps && !hasMtls && hasLoadedData) {
        rows.push(
          <tr key={`empty-${mtlKey}`}>
            <td
              colSpan={8}
              className="px-3 py-2 text-sm border-b border-gray-200 text-gray-500 italic border-l-4 border-l-gray-300"
              style={{ paddingLeft: (depth + 1) * 32 + 12 }}
            >
              {t('parts.method.noChildren')}
            </td>
          </tr>
        );
      }
    }

    return rows;
  };

  const hasOperations =
    methodData.operations && methodData.operations.length > 0;
  const hasMaterials = methodData.materials && methodData.materials.length > 0;

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm flex flex-col">
      {/* Header */}
      <div className="bg-blue-50 px-4 py-3 border-b border-blue-200 flex-shrink-0 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-blue-900 inline-flex items-center gap-2">
            <span>{t('parts.method.title')}</span>
            {/* Approval Status Indicator */}
            {methodData.approved ? (
              <span className="relative group/pill inline-flex items-center">
                <span className="px-2 py-0.5 text-xs font-semibold bg-green-100 text-green-800 rounded-full border border-green-300 cursor-default leading-none">
                  APPROVED
                </span>
                {methodData.approvedBy && (
                  <span className="absolute left-0 top-full mt-1 px-2 py-1 text-xs font-normal bg-gray-800 text-white rounded shadow-lg whitespace-nowrap opacity-0 group-hover/pill:opacity-100 transition-opacity duration-100 z-10 pointer-events-none">
                    Approved by {methodData.approvedBy}
                    {methodData.approvedDate &&
                      ` on ${parseCalendarDate(methodData.approvedDate).toLocaleDateString()}`}
                  </span>
                )}
              </span>
            ) : (
              <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-800 rounded-full border border-red-300 leading-none">
                NOT APPROVED
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-xs text-blue-700">
              {methodData.drawNum} - Rev {methodData.revisionNum}
            </p>
            {methodData.drawNum?.trim() && (
              <button
                onClick={() => {
                  const drawNum = methodData.drawNum?.trim();
                  if (drawingAvailable && drawNum) {
                    apiService.openDrawingPdf(drawNum);
                  }
                }}
                disabled={drawingAvailable !== true}
                className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title={
                  drawingAvailable
                    ? t('parts.method.drawing')
                    : t('parts.method.drawingUnavailable')
                }
              >
                {drawingAvailable === null ? (
                  <div className="w-3 h-3 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
                ) : (
                  <FileText size={12} />
                )}
                {t('parts.method.drawing')}
                {drawingAvailable && <ExternalLink size={10} />}
              </button>
            )}
          </div>
        </div>
        {hasExpandableItems && (
          <div className="flex gap-2">
            <button
              onClick={handleExpandAll}
              disabled={isExpandingAll}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('parts.method.expandAll')}
            >
              {isExpandingAll ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <ChevronsUpDown size={14} />
                  {t('parts.method.expandAll')}
                </>
              )}
            </button>
            <button
              onClick={handleCollapseAll}
              disabled={isExpandingAll || !hasAnyExpanded}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-300 rounded hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('parts.method.collapseAll')}
            >
              <ChevronsDownUp size={14} />
              {t('parts.method.collapseAll')}
            </button>
          </div>
        )}
      </div>

      {/* Content - Table */}
      <div className="flex-grow overflow-auto">
        <table className="w-full min-w-[900px]">
          <colgroup>
            <col className="w-52" />
            <col />
            <col className="w-20" />
            <col className="w-20" />
            <col className="w-20" />
            <col className="w-24" />
            <col className="w-20" />
            <col className="w-24" />
          </colgroup>
          <tbody>
            {/* Root operations section */}
            {hasOperations && (
              <>
                {renderOperationsHeader(
                  methodData.operations.length,
                  0,
                  'root'
                )}
                {methodData.operations.map((op, idx) =>
                  renderOperationRow(op, 0, 'root', idx)
                )}
              </>
            )}

            {/* Root materials section */}
            {hasMaterials && (
              <>
                {renderMaterialsHeader(methodData.materials.length, 0, 'root')}
                {methodData.materials.map((mtl, idx) =>
                  renderMaterialRow(mtl, 0, 'root', idx)
                )}
              </>
            )}

            {/* Empty state */}
            {!hasOperations && !hasMaterials && (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-8 text-center text-gray-500 italic"
                >
                  {t('parts.method.noChildren')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MethodTree;
